import { useState } from 'react';
import { supabase } from './supabase';
import PrivacyModal from './PrivacyModal';

const TEAL = '#0eb8a0';

const S = {
  wrap: { minHeight:'100vh', background:'#111112', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Share Tech Mono','Courier New',monospace", padding:'1rem' },
  card: { background:'#18181a', border:'1px solid #2e2e33', borderRadius:6, width:'100%', maxWidth:420, boxShadow:'0 24px 80px rgba(0,0,0,.9)' },
  top:  { padding:'2rem 2rem 1.5rem', borderBottom:'1px solid #2e2e33', textAlign:'center' },
  body: { padding:'1.75rem 2rem' },
  foot: { padding:'1rem 2rem 1.75rem', borderTop:'1px solid #2e2e33', textAlign:'center' },
};

const inputStyle = {
  background:'#0d0d0e', border:'1px solid #2e2e33', color:'#d4d2cc',
  fontFamily:'inherit', fontSize:'16px', letterSpacing:'.04em',
  padding:'.65rem .85rem', borderRadius:4, outline:'none',
  width:'100%', boxShadow:'inset 0 1px 3px rgba(0,0,0,.4)',
  marginTop:'.36rem', boxSizing:'border-box', WebkitAppearance:'none',
};
const labelStyle = { fontSize:'.58rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.16em', display:'block' };

// ── Login lockout helpers ────────────────────────────────────────────────────
const LOCKOUT_KEY      = 'gv_lockout';
const MAX_ATTEMPTS     = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes in ms

function getLockout(email) {
  try {
    const raw = localStorage.getItem(LOCKOUT_KEY);
    const data = raw ? JSON.parse(raw) : {};
    return data[email.toLowerCase()] || { attempts: 0, lockedUntil: null };
  } catch { return { attempts: 0, lockedUntil: null }; }
}

function setLockout(email, record) {
  try {
    const raw = localStorage.getItem(LOCKOUT_KEY);
    const data = raw ? JSON.parse(raw) : {};
    data[email.toLowerCase()] = record;
    localStorage.setItem(LOCKOUT_KEY, JSON.stringify(data));
  } catch {}
}

function clearLockout(email) {
  try {
    const raw = localStorage.getItem(LOCKOUT_KEY);
    const data = raw ? JSON.parse(raw) : {};
    delete data[email.toLowerCase()];
    localStorage.setItem(LOCKOUT_KEY, JSON.stringify(data));
  } catch {}
}

export default function AuthScreen() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [mode, setMode]       = useState('login');
  const [email, setEmail]     = useState('');
  const [password, setPass]   = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg]         = useState(null);
  const [lockRemaining, setLockRemaining] = useState(0); // seconds left

  function flash(text, type='err') { setMsg({ text, type }); }

  // Countdown timer while locked out
  useState(() => {
    const interval = setInterval(() => {
      if (!email.trim()) return;
      const rec = getLockout(email.trim());
      if (rec.lockedUntil) {
        const remaining = Math.max(0, Math.ceil((rec.lockedUntil - Date.now()) / 1000));
        setLockRemaining(remaining);
        if (remaining === 0) {
          clearLockout(email.trim());
          setMsg(null);
        }
      } else {
        setLockRemaining(0);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [email]);

  async function handleSubmit() {
    setMsg(null);
    if(!email.trim()) { flash('Email is required.'); return; }

    if(mode==='reset') {
      setLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: window.location.origin });
      setLoading(false);
      if(error) flash(error.message);
      else flash('Check your email for a password reset link.', 'ok');
      return;
    }

    if(!password) { flash('Password is required.'); return; }

    // ── Lockout check (login only) ───────────────────────────────────────────
    if (mode === 'login') {
      const rec = getLockout(email.trim());
      if (rec.lockedUntil && Date.now() < rec.lockedUntil) {
        const mins = Math.ceil((rec.lockedUntil - Date.now()) / 60000);
        flash(`Account locked. Try again in ${mins} minute${mins !== 1 ? 's' : ''}.`);
        return;
      }
    }

    if(mode==='signup') {
      if(password.length<8) { flash('Password must be at least 8 characters.'); return; }
      if(password!==confirm) { flash('Passwords do not match.'); return; }
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({ email: email.trim(), password });
      setLoading(false);
      if(error) { flash(error.message); return; }
      if(data?.user) {
        localStorage.setItem('gearvault-session-user', JSON.stringify(data.user));
        window.location.reload();
      }
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setLoading(false);

    if (error) {
      // ── Track failed attempts ──────────────────────────────────────────────
      const rec = getLockout(email.trim());
      const attempts = (rec.attempts || 0) + 1;
      const remaining = MAX_ATTEMPTS - attempts;

      if (attempts >= MAX_ATTEMPTS) {
        const lockedUntil = Date.now() + LOCKOUT_DURATION;
        setLockout(email.trim(), { attempts, lockedUntil });
        setLockRemaining(LOCKOUT_DURATION / 1000);
        flash('Too many failed attempts. Account locked for 15 minutes.');
      } else {
        setLockout(email.trim(), { attempts, lockedUntil: null });
        const warn = remaining === 1 ? ' — 1 attempt left before lockout' : ` — ${remaining} attempts left`;
        flash(error.message + warn);
      }
    } else {
      // ── Clear on success ───────────────────────────────────────────────────
      clearLockout(email.trim());
    }
    // on success, App.jsx auth listener fires → GearVault mounts
  }

  function handleKey(e) { if(e.key==='Enter') handleSubmit(); }
  const titles = { login:'Sign In', signup:'Create Account', reset:'Reset Password' };
  const subs   = { login:'Access your gear vault', signup:'Start your free vault', reset:"We'll email you a link" };

  return (
    <div style={S.wrap}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Black+Ops+One&family=Share+Tech+Mono&display=swap');*{box-sizing:border-box;margin:0;padding:0}input{-webkit-appearance:none;appearance:none}`}</style>
      <div style={S.card}>
        {/* Logo */}
        <div style={S.top}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'.75rem', marginBottom:'.85rem' }}>
            <svg width="42" height="42" viewBox="0 0 32 32" fill="none" style={{ filter:`drop-shadow(0 0 8px rgba(14,184,160,.45))` }}>
              <path d="M16 2 C16 2, 28 8, 28 18 C28 24, 22 26, 16 30 C10 26, 4 24, 4 18 C4 8, 16 2, 16 2Z"
                fill="#0a1f1e" stroke={TEAL} strokeWidth="1.4" strokeLinejoin="round"/>
              <path d="M16 6 C16 6, 24 11, 24 18" stroke={TEAL} strokeWidth=".6" strokeLinecap="round" opacity=".4"/>
              <circle cx="16" cy="17" r="2" fill={TEAL} opacity=".75"/>
            </svg>
            <div style={{ fontFamily:"'Black Ops One',impact,sans-serif", fontSize:'1.7rem', letterSpacing:'.06em', color:'#f0ede6', lineHeight:1 }}>
              Gear<span style={{ color:TEAL }}>Vault</span>
            </div>
          </div>
          <div style={{ fontFamily:"'Black Ops One',impact,sans-serif", fontSize:'1.05rem', letterSpacing:'.06em', color:'#f0ede6', marginBottom:'.25rem' }}>{titles[mode]}</div>
          <div style={{ fontSize:'.6rem', color:'#7a7872', letterSpacing:'.15em', textTransform:'uppercase' }}>{subs[mode]}</div>
        </div>

        {/* Form */}
        <div style={S.body}>
          {msg && (
            <div style={{ background:msg.type==='ok'?'rgba(14,184,160,.08)':'rgba(224,64,64,.08)', border:`1px solid ${msg.type==='ok'?'rgba(14,184,160,.3)':'rgba(224,64,64,.3)'}`, borderRadius:3, padding:'.65rem .85rem', fontSize:'.72rem', color:msg.type==='ok'?TEAL:'#e04040', marginBottom:'1rem', lineHeight:1.5 }}>
              {msg.text}
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:'.9rem' }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={handleKey} placeholder="you@example.com" autoFocus />
            </div>
            {mode!=='reset' && (
              <div>
                <label style={labelStyle}>Password</label>
                <input style={inputStyle} type="password" value={password} onChange={e=>setPass(e.target.value)} onKeyDown={handleKey} placeholder={mode==='signup'?'Min. 8 characters':'••••••••'} />
              </div>
            )}
            {mode==='signup' && (
              <div>
                <label style={labelStyle}>Confirm Password</label>
                <input style={inputStyle} type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} onKeyDown={handleKey} placeholder="••••••••" />
              </div>
            )}
            {lockRemaining > 0 && (
              <div style={{ background:'rgba(224,64,64,.08)', border:'1px solid rgba(224,64,64,.3)', borderRadius:3, padding:'.65rem .85rem', fontSize:'.7rem', color:'#e04040', textAlign:'center', lineHeight:1.6 }}>
                🔒 Locked — {Math.floor(lockRemaining/60)}:{String(lockRemaining%60).padStart(2,'0')} remaining
              </div>
            )}
            <button onClick={handleSubmit} disabled={loading || lockRemaining > 0}
              style={{ fontFamily:'inherit', fontSize:'.75rem', letterSpacing:'.1em', textTransform:'uppercase', padding:'.7rem 1rem', borderRadius:4, cursor:(loading||lockRemaining>0)?'not-allowed':'pointer', border:'none', background:lockRemaining>0?'#2e2e33':loading?'#0a7a6d':TEAL, color:lockRemaining>0?'#5a5a5a':'#000', fontWeight:700, boxShadow:lockRemaining>0?'none':'0 2px 12px rgba(14,184,160,.4)', transition:'background .15s', marginTop:'.25rem', minHeight:46 }}>
              {loading ? '…' : lockRemaining > 0 ? 'Locked' : titles[mode]}
            </button>
          </div>
        </div>

        {/* Footer — auth links */}
        <div style={S.foot}>
          {mode==='login' && <>
            <span style={{ fontSize:'.68rem', color:'#7a7872' }}>No account? </span>
            <button onClick={()=>{setMode('signup');setMsg(null);}} style={{ fontFamily:'inherit', fontSize:'.68rem', color:TEAL, background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>Sign up free</button>
            <span style={{ fontSize:'.68rem', color:'#2e2e33', margin:'0 .5rem' }}>·</span>
            <button onClick={()=>{setMode('reset');setMsg(null);}} style={{ fontFamily:'inherit', fontSize:'.68rem', color:'#7a7872', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>Forgot password?</button>
          </>}
          {mode==='signup' && <>
            <span style={{ fontSize:'.68rem', color:'#7a7872' }}>Already have an account? </span>
            <button onClick={()=>{setMode('login');setMsg(null);}} style={{ fontFamily:'inherit', fontSize:'.68rem', color:TEAL, background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>Sign in</button>
          </>}
          {mode==='reset' && (
            <button onClick={()=>{setMode('login');setMsg(null);}} style={{ fontFamily:'inherit', fontSize:'.68rem', color:'#7a7872', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>← Back to sign in</button>
          )}
        </div>

        {/* Site footer */}
        <div style={{ borderTop:'1px solid #1e1e20', padding:'.75rem 1.5rem .5rem', textAlign:'center' }}>
          <div style={{ fontSize:'.52rem', color:'#2e2e33', letterSpacing:'.08em', marginBottom:'.4rem' }}>
            gearvault.xyz · Built for musicians, by musicians.
          </div>
          <button onClick={()=>setShowPrivacy(true)} style={{
            background:'none', border:'none', cursor:'pointer',
            fontSize:'.5rem', color:'#3a3a3a', letterSpacing:'.1em',
            textTransform:'uppercase', textDecoration:'underline',
            fontFamily:"'Share Tech Mono','Courier New',monospace", padding:0,
          }}>Privacy Policy</button>
        </div>
      </div>
      {showPrivacy && <PrivacyModal onClose={()=>setShowPrivacy(false)}/>}
    </div>
  );
}
